import { useCallback, useEffect, useRef, useState } from "react";
import BriefPickCard from "./BriefPickCard";
import { apiFetch } from "./apiConfig";
import "./brief.css";

// Priority is always shown as a word; the badge tone only reinforces it.
const PRIORITY_STYLES = {
  high: { label: "High priority", tone: "danger" },
  medium: { label: "Medium priority", tone: "warning" },
  low: { label: "Low priority", tone: "" },
};

function priorityStyle(priority) {
  return PRIORITY_STYLES[priority] || { label: priority || "Priority", tone: "" };
}

function badgeClass(tone) {
  return tone ? `gd-badge gd-badge--${tone}` : "gd-badge";
}

// Lucide outline icons (24x24 viewBox), rendered at the system's 20px / 1.5 stroke.
const ICON_PATHS = {
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </>
  ),
  "circle-check": (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  "triangle-alert": (
    <>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </>
  ),
  "circle-alert": (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </>
  ),
  circle: <circle cx="12" cy="12" r="10" />,
  "circle-dot": (
    <>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="1" />
    </>
  ),
  "circle-x": (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </>
  ),
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
};

function Icon({ name }) {
  return (
    <svg
      className="gd-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

/** Inline message with its icon: the tone is never carried by color alone. */
function Notice({ tone, icon, role, title, children }) {
  return (
    <div className={tone ? `gd-notice gd-notice--${tone}` : "gd-notice"} role={role}>
      <span className="gd-notice__icon">
        <Icon name={icon} />
      </span>
      <div className="gd-notice__body">
        {title ? <div className="gd-notice__title">{title}</div> : null}
        {children}
      </div>
    </div>
  );
}

// Step icon / state per need phase in the running checklist.
function stepState(status) {
  if (status.phase === "done") return status.judgeFailed ? "failed" : "done";
  if (status.phase === "searching" || status.phase === "judging") return "active";
  return "pending";
}

const STEP_ICONS = {
  pending: "circle",
  active: "circle-dot",
  done: "circle-check",
  failed: "circle-x",
};

function initialNeedStatus() {
  return { phase: "pending", searchesDone: 0, pickCount: null, judgeFailed: false, judgeFailureReason: null };
}

/**
 * Computes an overall progress reading for the running phase from data we
 * already track. Deliberately simple: before `decompose_done` we don't know
 * the need count, so we report an indeterminate state rather than a fake
 * percentage. After that, need-based progress is capped at 90% (thumbnail
 * fetching is a distinct, separately-tracked remaining stretch), using
 * per-need phase as a coarse fractional credit (pending=0, searching=0.3,
 * judging=0.65, done=1) rather than counting exact search-variant completions
 * — simpler and still monotonic/representative, without over-claiming
 * precision we don't have.
 */
function computeProgress({ needsChecklist, needStatus, thumbStatus }) {
  if (thumbStatus?.phase === "done") return { mode: "determinate", pct: 100 };
  if (thumbStatus?.phase === "loading") return { mode: "thumbnails", pct: 90 };
  if (needsChecklist.length === 0) return { mode: "indeterminate", pct: 0 };

  const total = needsChecklist.length;
  let credit = 0;
  for (const need of needsChecklist) {
    const phase = needStatus[need.id]?.phase;
    if (phase === "done") credit += 1;
    else if (phase === "judging") credit += 0.65;
    else if (phase === "searching") credit += 0.3;
  }
  return { mode: "determinate", pct: Math.round((credit / total) * 90) };
}

/** Measured progress bar for the running phase. Rendered only when there is a
 * real percentage; before the need count is known the stage text says what is
 * happening in words instead (the design system has no indeterminate bar). */
function ProgressBar({ progress }) {
  const { mode, pct } = progress;
  if (mode === "indeterminate") return null;
  return (
    <div className="gd-stack gd-stack--tight">
      <div className="gd-row gd-row--between">
        <span className="label">Report generation progress</span>
        <span className="small gd-muted brief-tabular">{pct}%</span>
      </div>
      <div
        className="gd-progress"
        role="progressbar"
        aria-label="Report generation progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className="gd-progress__bar" style={{ inlineSize: `${pct}%` }} />
      </div>
    </div>
  );
}

// Must match backend/briefJobs.js's WEB_LOOKUP_BUDGET_PER_JOB — used only as
// this component's optimistic starting display (every report legitimately
// starts unspent) before the first web-reference response tells it the real
// count; the server is the actual source of truth for enforcement.
const WEB_LOOKUP_BUDGET_PER_JOB = 10;

function WebLookupBudgetNote({ remaining }) {
  return (
    <p className="small gd-muted">
      {remaining} of {WEB_LOOKUP_BUDGET_PER_JOB} web lookups remaining for this report
    </p>
  );
}

const PHASE_LABELS = {
  pending: "Queued",
  searching: "Searching…",
  judging: "Judging candidates…",
  done: "Done",
};

function CoverageBanner({ coverage }) {
  const covered = coverage?.covered ?? 0;
  const total = coverage?.total ?? 0;
  const unjudged = coverage?.unjudged ?? 0;
  const allCovered = total > 0 && covered === total;
  const tone = allCovered ? "success" : covered > 0 ? "warning" : "danger";
  const icon = allCovered ? "circle-check" : covered > 0 ? "triangle-alert" : "circle-alert";
  return (
    <Notice
      tone={tone}
      icon={icon}
      role="status"
      title={`${covered} of ${total} need${total === 1 ? "" : "s"} covered with a reference${
        unjudged > 0 ? ` · ${unjudged} could not be judged` : ""
      }`}
    />
  );
}

/**
 * Persistent (not just running-phase) note that the document was truncated
 * and/or its decomposed needs were capped — see docTruncated/needsCapped
 * state above, set from the doc_truncated / needs_capped SSE events. On a
 * fast run (common on MiMo) the running phase can flash by in a few seconds,
 * so this same information needs to survive into the "done" render too, not
 * just live alongside the progress UI. Renders nothing when neither happened.
 */
function TruncationNote({ docTruncated, needsCapped }) {
  if (!docTruncated && !needsCapped) return null;
  const parts = [];
  if (docTruncated) {
    parts.push(
      `Document text was truncated — analyzed the first ${docTruncated.truncatedToCharCount.toLocaleString()} of ${docTruncated.originalCharCount.toLocaleString()} characters.`
    );
  }
  if (needsCapped) {
    parts.push(
      `Design needs were capped — analyzed the first ${needsCapped.cappedTo} of ${needsCapped.originalCount} needs identified.`
    );
  }
  return (
    <Notice tone="warning" icon="triangle-alert" role="status">
      {parts.join(" ")} This report may be based on incomplete input.
    </Notice>
  );
}

function GapNote({ need }) {
  if (need.judgeFailed) {
    return (
      <Notice tone="danger" icon="circle-alert" title="Could not be judged">
        This is not a "nothing fits" result: no candidate was evaluated. Running the report again may help.
        {need.judgeFailureReason ? <div className="brief-notice-detail">{need.judgeFailureReason}</div> : null}
      </Notice>
    );
  }
  return (
    <Notice icon="info">No adequate reference was found in the existing corpus for this need.</Notice>
  );
}

/** Small badge distinguishing a "screen" reference (a real UI to look at) from
 * "guidance" (written pattern advice/documentation) — see webReferences.js. */
function WebReferenceKindBadge({ kind }) {
  const isScreen = kind === "screen";
  return <span className={badgeClass(isScreen ? "accent" : "")}>{isScreen ? "Screen" : "Guidance"}</span>;
}

/** One found web reference: title link, source, kind badge, rationale, and
 * (when known) the page's age. pageAge is null when the search tool didn't
 * report one — that's a real "unknown", not a value, so it's simply omitted
 * rather than shown as a fabricated fallback string. */
function WebReferenceRow({ webRef }) {
  return (
    <li className="gd-list__item">
      <div className="gd-list__main gd-stack gd-stack--tight">
        <div className="gd-row gd-row--wrap">
          <a
            className="body-strong brief-webref__link"
            href={webRef.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {webRef.title || webRef.url}
          </a>
          <WebReferenceKindBadge kind={webRef.kind} />
        </div>
        {webRef.source ? <p className="small gd-muted brief-wrap">{webRef.source}</p> : null}
        {webRef.rationale ? <p className="small brief-wrap">{webRef.rationale}</p> : null}
        {webRef.pageAge ? <p className="caption gd-muted">{webRef.pageAge}</p> : null}
      </div>
    </li>
  );
}

/**
 * "Find web references" action for a single Design need — issue #6's tracer
 * bullet. Local state only (status/references/errorMessage), scoped to this
 * one card via a plain useState in this component — deliberately NOT lifted
 * to a shared/global map (the report-view data layer already avoided that
 * shape, see issue #5, and the UI should keep that property).
 *
 * Visual weight is deliberately different depending on `prominent`:
 * - understated (has picks): a ghost button — the Figma corpus stays the
 *   primary answer, this shouldn't compete with the picks grid for attention.
 * - prominent (a Gap, no picks): a secondary button — a Gap is currently a
 *   dead end, and this is the next step being offered.
 */
function WebReferencesAction({ need, jobId, prominent, onBudgetUpdate }) {
  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'done' | 'error' | 'exhausted'
  const [references, setReferences] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  // `refresh` distinguishes "show me what's there" (free — a cache hit costs
  // nothing) from "Search again" (a deliberate re-spend that always makes a
  // fresh paid call and overwrites the cached result). The very first click
  // on a need is always a non-refresh request; the server itself decides
  // whether that's a cache hit or a fresh lookup — see briefRoutes.js.
  const handleClick = async (refresh) => {
    setStatus("loading");
    setErrorMessage(null);
    try {
      const res = await apiFetch(`/api/brief/${jobId}/needs/${need.id}/web-references`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      const data = await res.json().catch(() => ({}));
      if (typeof data.remaining === "number") {
        onBudgetUpdate?.(data.remaining);
      }
      if (res.status === 429 || data.budgetExhausted) {
        setErrorMessage(data.message || "You've used all your web lookups for this report.");
        setStatus("exhausted");
        return;
      }
      if (!res.ok || data.ok === false) {
        throw new Error(data.message || `Could not find web references (${res.status})`);
      }
      setReferences(Array.isArray(data.references) ? data.references : []);
      setStatus("done");
    } catch (err) {
      setErrorMessage(err?.message || "Something went wrong finding web references.");
      setStatus("error");
    }
  };

  const buttonLabel = status === "done" ? "Search again" : "Find web references";
  const onButtonClick = () => handleClick(status === "done");

  return (
    <div className="brief-webrefs gd-stack">
      {status === "loading" ? (
        <div className="gd-row small gd-muted" role="status">
          <Icon name="search" />
          <span>Searching the web…</span>
        </div>
      ) : status === "exhausted" ? null : (
        <button
          type="button"
          onClick={onButtonClick}
          className={prominent ? "gd-btn" : "gd-btn gd-btn--ghost"}
        >
          {buttonLabel}
        </button>
      )}

      {status === "error" ? (
        <Notice tone="danger" icon="circle-alert" role="alert">
          {errorMessage}
        </Notice>
      ) : null}

      {status === "exhausted" ? (
        // Deliberately NOT styled as an error (neutral tone) — this is an
        // expected, correctly-enforced limit, not a failure.
        <Notice icon="info" role="status">
          {errorMessage}
        </Notice>
      ) : null}

      {status === "done" ? (
        references.length === 0 ? (
          <p className="small gd-muted">No web references found in the allowed sources for this need.</p>
        ) : (
          <ul className="gd-list">
            {references.map((webRef, i) => (
              <WebReferenceRow key={`${need.id}-webref-${i}`} webRef={webRef} />
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

function NeedResultCard({ need, jobId, onWebLookupBudgetUpdate }) {
  const pStyle = priorityStyle(need.priority);
  const hasPicks = Array.isArray(need.picks) && need.picks.length > 0;
  return (
    <section className="gd-card gd-stack">
      <div className="gd-card__head">
        <h3 className="gd-card__title brief-wrap">{need.title}</h3>
        <span className={badgeClass(pStyle.tone)}>{pStyle.label}</span>
      </div>
      {need.description ? <p className="body gd-muted brief-wrap">{need.description}</p> : null}

      {hasPicks ? (
        <div className="brief-picks">
          {need.picks.map((pick, i) => (
            <BriefPickCard key={`${need.id}-${i}`} pick={pick} />
          ))}
        </div>
      ) : (
        <GapNote need={need} />
      )}

      <WebReferencesAction
        need={need}
        jobId={jobId}
        prominent={!hasPicks}
        onBudgetUpdate={onWebLookupBudgetUpdate}
      />
    </section>
  );
}

/** Formats a byte count as a human-readable KB/MB string, e.g. "240 KB" or "1.3 MB". */
function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** Formats a whole-second elapsed duration as "m:ss", e.g. 64 -> "1:04". */
function formatElapsed(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Honest "still normal" reassurance copy for the pre-decompose_done stretch,
// keyed by how long we've been waiting with no need count yet. Deliberately
// avoids implying a percentage or near-completion — just confirms nothing's stuck.
const REASSURANCE_THRESHOLDS = [
  { atSeconds: 30, text: "This one's a big document, hang tight." },
  { atSeconds: 15, text: "Still reading your document — larger files take longer." },
];

function reassuranceText(elapsedSeconds) {
  const hit = REASSURANCE_THRESHOLDS.find((t) => elapsedSeconds >= t.atSeconds);
  return hit ? hit.text : null;
}

/**
 * Either `file` (+ optional `purpose`): uploads the document and starts the run
 * itself. Or `startedJob` ({ jobId, streamToken }): a report the search box
 * already started from Request text (no file, no purpose); this view only
 * follows its stream. A text run has no `onRetry`: its analysis is single-use.
 */
export default function BriefView({ file, purpose, startedJob, onRetry, onStartNew, onBack }) {
  const isTextRun = !!startedJob; // started from Request text: no document wording, no retry
  const [phase, setPhase] = useState("running"); // 'running' | 'done' | 'error'

  const [docExtracted, setDocExtracted] = useState(null);
  // Set only when the server actually truncated the document/needs list (see
  // the doc_truncated / needs_capped SSE events) — null means "didn't happen",
  // not "haven't heard yet", so these render nothing on a normal run.
  const [docTruncated, setDocTruncated] = useState(null); // {originalCharCount, truncatedToCharCount}
  const [needsCapped, setNeedsCapped] = useState(null); // {originalCount, cappedTo}
  const [needsChecklist, setNeedsChecklist] = useState([]);
  const [needStatus, setNeedStatus] = useState({});
  const [currentNeed, setCurrentNeed] = useState(null); // {needIndex, total, title}
  const [stageLabel, setStageLabel] = useState(isTextRun ? "Starting your report…" : "Uploading document…");
  const [thumbStatus, setThumbStatus] = useState(null); // {phase:'loading'|'done', frameCount, succeeded, failed}
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const [report, setReport] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorStage, setErrorStage] = useState(null);

  // Set alongside openStream() in the submit-on-mount effect's success path —
  // the HTML export button (see handleDownloadHtml below) needs this once the
  // report is ready, and it isn't otherwise stored anywhere after the SSE
  // stream closes.
  const [jobId, setJobId] = useState(null);
  const [isDownloadingHtml, setIsDownloadingHtml] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [webLookupsRemaining, setWebLookupsRemaining] = useState(WEB_LOOKUP_BUDGET_PER_JOB);

  const esRef = useRef(null);

  const closeStream = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
  }, []);

  // Belt-and-braces: also close on unmount (e.g. user switches back to
  // search view mid-run).
  useEffect(() => closeStream, [closeStream]);

  // Ticking elapsed-time counter — proof-of-life for the whole running phase,
  // not just the pre-decompose stretch. Starts at 0 the moment we enter
  // "running" and stops (interval cleared) on any other phase or unmount.
  useEffect(() => {
    if (phase !== "running") return undefined;
    setElapsedSeconds(0);
    const id = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const handleEvent = useCallback((data) => {
    switch (data.type) {
      case "doc_extracted":
        setDocExtracted({ charCount: data.charCount });
        setStageLabel("Reading requirements…");
        break;
      case "doc_truncated":
        setDocTruncated({
          originalCharCount: data.originalCharCount,
          truncatedToCharCount: data.truncatedToCharCount,
        });
        break;
      case "decompose_start":
        // A text run already has its needs; there is nothing to break up.
        if (!isTextRun) setStageLabel("Breaking the document into design needs…");
        break;
      case "needs_capped":
        setNeedsCapped({ originalCount: data.originalCount, cappedTo: data.cappedTo });
        break;
      case "decompose_done": {
        const needs = Array.isArray(data.needs) ? data.needs : [];
        setNeedsChecklist(needs);
        const initial = {};
        for (const n of needs) initial[n.id] = initialNeedStatus();
        setNeedStatus(initial);
        setStageLabel(null);
        break;
      }
      case "need_start":
        setCurrentNeed({ needIndex: data.needIndex, total: data.total, title: data.title });
        setNeedStatus((prev) => ({
          ...prev,
          [data.needId]: { ...(prev[data.needId] || initialNeedStatus()), phase: "searching" },
        }));
        break;
      case "search_done":
        setNeedStatus((prev) => {
          const cur = prev[data.needId] || initialNeedStatus();
          return { ...prev, [data.needId]: { ...cur, searchesDone: cur.searchesDone + 1 } };
        });
        break;
      case "judge_start":
        setNeedStatus((prev) => ({
          ...prev,
          [data.needId]: { ...(prev[data.needId] || initialNeedStatus()), phase: "judging" },
        }));
        break;
      case "judge_done":
        setNeedStatus((prev) => ({
          ...prev,
          [data.needId]: {
            ...(prev[data.needId] || initialNeedStatus()),
            phase: "done",
            pickCount: data.pickCount,
            judgeFailed: !!data.judgeFailed,
            judgeFailureReason: data.judgeFailureReason || null,
          },
        }));
        break;
      case "pipeline_done":
        setCurrentNeed(null);
        setStageLabel("Wrapping up…");
        break;
      case "thumbnails_start":
        setThumbStatus({ phase: "loading", frameCount: data.frameCount });
        setStageLabel("Fetching thumbnails from Figma…");
        break;
      case "thumbnails_done":
        setThumbStatus((prev) => ({ ...(prev || {}), phase: "done", succeeded: data.succeeded, failed: data.failed }));
        setStageLabel("Building report…");
        break;
      case "report_ready":
        setReport(data.report);
        setPhase("done");
        closeStream();
        break;
      case "job_error":
        // A text run has no Try again button (its analysis is single-use), so the
        // server's "Please try again." would point at nothing.
        setErrorMessage(
          (data.message || "Something went wrong while generating the report.").replace(
            /Please try again\./g,
            isTextRun ? "Go back and search again." : "Please try again."
          )
        );
        setErrorStage(data.stage || null);
        setPhase("error");
        closeStream();
        break;
      default:
        break;
    }
  }, [closeStream, isTextRun]);

  const openStream = useCallback((jobId, streamToken) => {
    const es = new EventSource(`/api/brief/${jobId}/events?token=${streamToken}`);
    esRef.current = es;
    es.onmessage = (evt) => {
      let data;
      try {
        data = JSON.parse(evt.data);
      } catch {
        return;
      }
      // Event type only: payloads can carry needs derived from pasted text.
      console.log("[brief]", data.type);
      handleEvent(data);
    };
    // Per the EventSource spec, a transient drop mid-stream (readyState goes
    // to CONNECTING) is recovered automatically by the browser's native
    // reconnect logic plus the server's replay buffer (briefJobs.js's
    // subscribe()) — deliberately no phase change for that case, same as
    // before. But a non-200 HTTP response (e.g. the job was TTL-swept, or the
    // server restarted) makes EventSource fail PERMANENTLY: readyState goes
    // to CLOSED and it never reconnects on its own. Without this check,
    // nothing would ever detect that and `phase` would stay "running" forever
    // with the spinner/elapsed timer just continuing.
    es.onerror = () => {
      if (es.readyState !== EventSource.CLOSED) {
        // Transient — mid-reconnect (CONNECTING). Let the browser keep trying.
        return;
      }
      closeStream();
      setErrorMessage(
        isTextRun
          ? "The connection was lost. Go back and search again."
          : "Lost connection to this report run — it may have expired or the server restarted. Please try again."
      );
      setErrorStage(null);
      setPhase("error");
    };
  }, [handleEvent, closeStream, isTextRun]);

  const startedRef = useRef(false);

  useEffect(() => {
    if (startedJob) {
      // Nothing to submit: just follow the stream. Unlike the upload below this
      // is safe to run twice (StrictMode's simulated remount closes the first
      // stream on cleanup; the server replays every event to the second).
      setJobId(startedJob.jobId);
      openStream(startedJob.jobId, startedJob.streamToken);
      return;
    }
    if (startedRef.current) return; // checked AND set synchronously, before any await
    startedRef.current = true;

    (async () => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        if (purpose) formData.append("purpose", purpose); // omit the field entirely when empty
        const res = await apiFetch("/api/brief", { method: "POST", body: formData });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) {
          throw new Error(data.message || `Could not start report (${res.status})`);
        }
        setJobId(data.jobId); // MUST be set — the HTML export depends on it later
        openStream(data.jobId, data.streamToken);
      } catch (err) {
        setErrorMessage(err?.message || "Something went wrong");
        setErrorStage("start");
        setPhase("error");
      }
    })();
    // Deliberately NO cleanup function returned from this effect, and deliberately
    // no `cancelled` flag checked inside the async closure. This file renders
    // under React.StrictMode (see frontend/src/index.js), which double-invokes
    // effects in development: mount → run effect → simulate unmount (cleanups
    // fire) → remount → effect runs again. The startedRef latch above is what
    // blocks the second, real invocation from double-POSTing (brief runs are
    // capped at 2 concurrent and rate-limited to 4/hour server-side, so a
    // double-fire here is genuinely expensive, not just wasteful).
    //
    // Do NOT "fix" this by adding a cleanup that sets a cancelled flag, or by
    // returning closeStream from this effect. Either change would let
    // StrictMode's simulated-unmount abort the FIRST run's in-flight POST/stream
    // while the latch simultaneously blocks the SECOND run from ever starting —
    // net result, the UI hangs forever at "Uploading document…". The existing
    // `useEffect(() => closeStream, [closeStream])` elsewhere in this file
    // already handles real teardown correctly and needs no change: at the
    // moment of StrictMode's simulated unmount, esRef.current is still null
    // (the POST hasn't resolved yet), so that cleanup is a harmless no-op.
    //
    // (No eslint-disable-next-line needed here: this project's build config
    // — eslint-config-react-app/base, since package.json has no eslintConfig
    // field — doesn't register the react-hooks plugin, so exhaustive-deps
    // isn't an active rule in the first place.)
  }, []);

  // Builds the standalone HTML export on the server (real work — fetching +
  // base64-embedding every pick's thumbnail so the file stays readable if
  // reopened months later, past Figma's ~30-day thumbnail URL expiry) and
  // downloads it client-side. Unlike the old in-memory markdown download,
  // this is a real network request, so it's not instant.
  const handleDownloadHtml = async () => {
    if (!jobId || isDownloadingHtml) return;
    setIsDownloadingHtml(true);
    setDownloadError(null);
    try {
      const res = await apiFetch(`/api/brief/${jobId}/html`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Could not generate the HTML export (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (report?.docName || "report").replace(/\.[^./\\]+$/, "").replace(/[^a-z0-9-_ ]/gi, "_");
      a.href = url;
      a.download = `${safeName || "report"}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err?.message || "Something went wrong generating the HTML export.");
    } finally {
      setIsDownloadingHtml(false);
    }
  };

  const gaps = Array.isArray(report?.gaps) ? report.gaps : [];
  // Needs the AI could not judge: listed apart from Gaps, which are honest "nothing suitable" results.
  const unjudged = Array.isArray(report?.unjudged) ? report.unjudged : [];
  const progress = computeProgress({ needsChecklist, needStatus, thumbStatus });
  const fileSizeLabel = file ? formatFileSize(file.size) : null;
  // Only shown before decompose_done — once real needs start streaming in,
  // the checklist itself is the concrete signal and this stops rendering.
  const reassurance =
    needsChecklist.length > 0
      ? null
      : isTextRun
        ? elapsedSeconds >= 30
          ? "Still working — this can take a minute."
          : null
        : reassuranceText(elapsedSeconds);
  const stageText = currentNeed
    ? `Analyzing need ${currentNeed.needIndex + 1} of ${currentNeed.total}…`
    : stageLabel || "Working…";
  const progressText = progress.mode === "indeterminate" ? "Progress is ongoing." : `${progress.pct}% complete.`;

  return (
    <div className="brief-root">
      {phase === "running" ? (
        <div className="gd-stack gd-stack--loose">
          <p role="status" aria-live="polite" aria-atomic="true" className="brief-sr-only">
            {stageText} {progressText}
          </p>
          <section className="gd-card gd-stack" aria-busy="true">
            <div className="gd-card__head">
              <h2 className="gd-card__title">{stageText}</h2>
              <span className="small gd-muted brief-tabular">{formatElapsed(elapsedSeconds)}</span>
            </div>
            <div className="gd-stack gd-stack--tight">
              {currentNeed ? (
                <p className="body brief-wrap">{currentNeed.title}</p>
              ) : stageLabel && needsChecklist.length > 0 ? (
                <p className="body brief-wrap">{stageLabel}</p>
              ) : null}
              {isTextRun ? (
                <p className="small gd-muted">Pasted text</p>
              ) : file ? (
                <p title={file.name} className="small gd-muted brief-ellipsis">
                  {file.name}
                  {fileSizeLabel ? ` · ${fileSizeLabel}` : ""}
                </p>
              ) : null}
              {reassurance ? <p className="small gd-muted">{reassurance}</p> : null}
              {docExtracted ? (
                <p className="small gd-muted">{docExtracted.charCount.toLocaleString()} characters extracted</p>
              ) : null}
              {docTruncated ? (
                <p className="small gd-muted">This document was long — only the first part was analyzed.</p>
              ) : null}
              {needsCapped ? (
                <p className="small gd-muted">
                  {isTextRun ? "This text" : "This document"} produced a lot of design needs — only the first{" "}
                  {needsCapped.cappedTo} were analyzed.
                </p>
              ) : null}
              {thumbStatus?.phase === "loading" ? (
                <p className="small gd-muted">
                  Fetching {thumbStatus.frameCount} thumbnail{thumbStatus.frameCount === 1 ? "" : "s"} from Figma
                  — this can take a while…
                </p>
              ) : null}
            </div>
            <ProgressBar progress={progress} />
          </section>

          {needsChecklist.length > 0 ? (
            <section className="gd-card">
              <ol className="gd-steps" aria-label="Design needs">
                {needsChecklist.map((need) => {
                  const status = needStatus[need.id] || initialNeedStatus();
                  const state = stepState(status);
                  const pStyle = priorityStyle(need.priority || "medium");
                  return (
                    <li key={need.id} className="gd-step" data-state={state}>
                      <span className="gd-step__icon">
                        <Icon name={STEP_ICONS[state]} />
                      </span>
                      <span className="gd-step__label brief-step-label">
                        <span className="brief-wrap">{need.title}</span>
                        <span className={badgeClass(pStyle.tone)}>{pStyle.label}</span>
                      </span>
                      <span className="gd-step__note small">
                        {status.phase === "done"
                          ? status.judgeFailed
                            ? "Could not be judged"
                            : `${status.pickCount ?? 0} reference${status.pickCount === 1 ? "" : "s"} found`
                          : PHASE_LABELS[status.phase]}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </section>
          ) : null}
        </div>
      ) : null}

      {phase === "error" ? (
        <div className="gd-stack">
          <Notice tone="danger" icon="circle-alert" role="alert" title="Report generation failed">
            <p>{errorMessage}</p>
            {errorStage ? <p className="small brief-notice-detail">Stage: {errorStage}</p> : null}
          </Notice>
          <div className="gd-row gd-row--wrap brief-actions">
            <button type="button" onClick={onBack} className="gd-btn">
              Back
            </button>
            {onRetry ? (
              <button type="button" onClick={onRetry} className="gd-btn gd-btn--primary">
                Try again
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {phase === "done" && report ? (
        <div className="gd-stack gd-stack--loose">
          <p role="status" aria-live="polite" aria-atomic="true" className="brief-sr-only">
            Report ready.
          </p>
          <header className="brief-header">
            <div className="brief-header__title">
              <h2 className="title brief-clamp">{report.docName || "Report"}</h2>
              {report.purpose ? <p className="small gd-muted brief-wrap">{report.purpose}</p> : null}
            </div>
            <div className="brief-header__actions">
              <div className="gd-row gd-row--wrap brief-actions">
                <button type="button" onClick={onStartNew} className="gd-btn">
                  New report
                </button>
                <button
                  type="button"
                  onClick={handleDownloadHtml}
                  disabled={isDownloadingHtml}
                  className="gd-btn gd-btn--primary"
                >
                  {isDownloadingHtml ? (
                    "Generating…"
                  ) : (
                    <>
                      <Icon name="download" />
                      Download as HTML
                    </>
                  )}
                </button>
              </div>
              {downloadError ? (
                <p className="gd-error brief-download-error" role="alert">
                  {downloadError}
                </p>
              ) : null}
            </div>
          </header>

          <div className="gd-stack">
            <CoverageBanner coverage={report.coverage} />
            <WebLookupBudgetNote remaining={webLookupsRemaining} />
            <TruncationNote
              docTruncated={report?.docTruncated ?? docTruncated}
              needsCapped={report?.needsCapped ?? needsCapped}
            />
          </div>

          {(report.needResults || []).map((need) => (
            <NeedResultCard
              key={need.id}
              need={need}
              jobId={jobId}
              onWebLookupBudgetUpdate={setWebLookupsRemaining}
            />
          ))}

          {gaps.length > 0 ? (
            <section className="gd-stack">
              <h3 className="heading">Gaps ({gaps.length})</h3>
              <ul className="gd-list">
                {gaps.map((need) => (
                  <li key={need.id} className="gd-list__item">
                    <div className="gd-list__main gd-stack gd-stack--tight">
                      <span className="gd-list__title brief-wrap">{need.title}</span>
                      <GapNote need={need} />
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {unjudged.length > 0 ? (
            <section className="gd-stack">
              <h3 className="heading">Could not be judged ({unjudged.length})</h3>
              <ul className="gd-list">
                {unjudged.map((need) => (
                  <li key={need.id} className="gd-list__item">
                    <div className="gd-list__main gd-stack gd-stack--tight">
                      <span className="gd-list__title brief-wrap">{need.title}</span>
                      <GapNote need={need} />
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
